import React, { useState, useEffect } from 'react';
import {
    Button,
    Form,
    Card,
    Typography,
    Tag,
    Row,
    Col,
    Flex,
    Space,
    message,
    Spin,
    Select,
    DatePicker,
    Switch,
    Divider,
    Input,
    Radio,
    Checkbox,
    Collapse
} from 'antd';
import { 
    CheckOutlined, 
    CloseOutlined, 
    UserOutlined, 
    CalendarOutlined,
    TeamOutlined,
    SaveOutlined,
    FilterOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useAttendance } from '../../hooks/useAttendance';
import { useAgeGroups } from '../../hooks/useAgeGroups';
import { useGroups } from '../../hooks/useGroups';
import useTrainers from '../../hooks/useTrainers';
import { addAttendance, updateAttendance } from '../../store/slices/attendanceSlice';
import { sendWpMessage } from '../../store/slices/wpConfigSlice';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const Attendance = () => {
    const dispatch = useDispatch();
    const selectedBranch = useSelector((state) => state.branches.selectedBranch);
    const selectedPlace = useSelector((state) => state.branches.selectedPlace);
    const { 
        students, 
        attendances, 
        loading, 
        error, 
        selectedFilter,
        fetchStudentsByAgeGroup,
        fetchStudentsByGroup,
        fetchAttendances,
        clearStudentsList,
        clearAttendancesList
    } = useAttendance();
    const { ageGroups } = useAgeGroups(selectedBranch?.id, selectedPlace?.id);
    const { groups } = useGroups(selectedBranch?.id, selectedPlace?.id);
    const { trainers } = useTrainers();
    
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [selectedAgeGroup, setSelectedAgeGroup] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [selectedTrainer, setSelectedTrainer] = useState(null);
    const [filterType, setFilterType] = useState('group'); // 'ageGroup' veya 'group'
    const [attendanceData, setAttendanceData] = useState([]);
    const [saving, setSaving] = useState(false);
    const [sendAbsenceMsg, setSendAbsenceMsg] = useState(true);
    const [absenceMessage, setAbsenceMessage] = useState('Sevgili velimiz, bugün antrenmanda öğrencimizi göremedik. Düzenli devam edilmemesi, antrenmanlarda geri kalmasına ve takım içerisindeki gelişiminin olumsuz etkilenmesine neden olabilir. Mazereti varsa bilgi vermenizi rica ederiz.');
    const [expandedNotes, setExpandedNotes] = useState({}); // Track which students have expanded note areas
    const [filterExpanded, setFilterExpanded] = useState(true); // Track filter section expansion

    // Trainer listesini yükle
    useEffect(() => {
        if (selectedBranch?.id) {
            // useTrainers hook'u zaten trainer listesini yüklüyor
        }
    }, [selectedBranch?.id]);

    // Seçili tarih değiştiğinde öğrencileri getir
    useEffect(() => {
        // Tüm gerekli parametreler mevcut olmalı
        if (selectedDate && selectedBranch) {
            if (filterType === 'ageGroup' && selectedAgeGroup) {
                fetchStudentsByAgeGroup(selectedAgeGroup, selectedDate);
            } else if (filterType === 'group' && selectedGroup) {
                fetchStudentsByGroup(selectedGroup, selectedDate);
            } else {
                // Grup veya yaş grubu seçili değilse attendanceData'yı temizle
                setAttendanceData([]);
                clearStudentsList();
                clearAttendancesList();
            }
        }
    }, [selectedDate, selectedAgeGroup, selectedGroup, filterType, selectedBranch]);

    // Öğrenci listesi değiştiğinde attendanceData'yı güncelle
    useEffect(() => {
        if (students && students.length > 0) {
            const initialAttendanceData = students.map(student => {
                // by age group API'si zaten yoklama verilerini içeren öğrenci listesi döndürüyor
                return {
                    id: student.id,
                    fullName: student.fullName,
                    status: student.isPresent ? 'present' : 'absent', // API'den gelen isPresent değerini kullan
                    note: student.note || '',
                    trainerFullName: student.trainerFullName || null,
                    attendanceDate: student.attendanceDate || null
                };
            });
            setAttendanceData(initialAttendanceData);
            // Note alanlarını yok olan öğrenciler için açık yap
            const initialExpandedNotes = {};
            students.forEach(student => {
                // Eğer öğrenci yok ise not alanını açık yap
                initialExpandedNotes[student.id] = !student.isPresent;
            });
            setExpandedNotes(initialExpandedNotes);
            // Öğrenciler yüklendiğinde filtre alanını kapat
            setFilterExpanded(false);
        } else {
            // Öğrenci listesi boşsa attendanceData'yı da temizle
            setAttendanceData([]);
            setExpandedNotes({});
        }
    }, [students]);

    const handleFilterChange = (type) => {
        setFilterType(type);
        setSelectedAgeGroup(null);
        setSelectedGroup(null);
        clearStudentsList();
        clearAttendancesList();
        setAttendanceData([]);
        setExpandedNotes({});
    };

    const handleAgeGroupChange = (ageGroupId) => {
        setSelectedAgeGroup(ageGroupId);
        setSelectedGroup(null);
        // Yaş grubu değiştiğinde attendanceData'yı temizle
        setAttendanceData([]);
        setExpandedNotes({});
    };

    const handleGroupChange = (groupId) => {
        setSelectedGroup(groupId);
        setSelectedAgeGroup(null);
        // Grup değiştiğinde attendanceData'yı temizle
        setAttendanceData([]);
        setExpandedNotes({});
    };

    const handleTrainerChange = (trainerId) => {
        setSelectedTrainer(trainerId);
    };

    const handleStatusChange = (studentId, status) => {
        setAttendanceData(prev =>
            prev.map(student =>
                student.id === studentId ? { ...student, status } : student
            )
        );
    };

    const handleNoteChange = (studentId, note) => {
        setAttendanceData(prev =>
            prev.map(student =>
                student.id === studentId ? { ...student, note } : student
            )
        );
    };

    const toggleNoteArea = (studentId) => {
        setExpandedNotes(prev => ({
            ...prev,
            [studentId]: !prev[studentId]
        }));
    };

    const handleBulkAttendance = (isPresent) => {
        if (!attendanceData || attendanceData.length === 0) {
            message.warning('Yoklama alınacak öğrenci bulunmamaktadır!');
            return;
        }

        const status = isPresent ? 'present' : 'absent';
        setAttendanceData(prev =>
            prev.map(student => ({ ...student, status }))
        );
        message.success(`Tüm öğrenciler ${isPresent ? 'var' : 'yok'} olarak işaretlendi!`);
    };

    const handleSaveAttendance = async () => {
        if (!attendanceData || attendanceData.length === 0) {
            message.warning('Kaydedilecek yoklama verisi bulunmamaktadır!');
            return;
        }

        setSaving(true);
        try {
            const selectedDateStr = selectedDate.format('YYYY-MM-DD');
            
            // Yeni model: Tek POST isteği ile tüm öğrencilerin yoklaması
            const attendancePayload = {
                trainerId: selectedTrainer || null, // Nullable for cases where trainer is not assigned
                date: selectedDateStr,
                studentAttendanceDtos: attendanceData.map(student => ({
                    studentId: student.id,
                    isPresent: student.status === 'present',
                    note: student.note || null
                }))
            };

            await dispatch(addAttendance(attendancePayload)).unwrap();
            message.success('Yoklama başarıyla kaydedildi!');
            
            // Yok işaretlenen öğrencilere veliye mesaj gönder
            if (sendAbsenceMsg) {
                // Yok olan öğrencilerin studentId'lerini topla
                const absentStudents = attendanceData.filter(m => m.status === 'absent');
                const absentStudentIds = absentStudents.map(m => m.id);
                console.log('absentStudentIds', absentStudentIds);
                console.log('absenceMessage', absenceMessage);
                if (absentStudentIds.length > 0) {
                    await dispatch(sendWpMessage({
                        studentIds: absentStudentIds,
                        message: absenceMessage
                    })).unwrap();
                    message.success('Yok olan öğrencilere veli mesajı gönderildi!');
                }
            }

            // Yoklama listesini yenile
            if (filterType === 'ageGroup' && selectedAgeGroup) {
                fetchStudentsByAgeGroup(selectedAgeGroup, selectedDate);
            } else if (filterType === 'group' && selectedGroup) {
                fetchStudentsByGroup(selectedGroup, selectedDate);
            }
        } catch (error) {
            console.error('Save attendance error:', error);
            message.error(`Yoklama kaydedilirken bir hata oluştu: ${error.message || error}`);
        } finally {
            setSaving(false);
        }
    };

    // Custom student list item component
    const StudentListItem = ({ student, index }) => (
        <div 
            key={student.id}
            style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 16px',
                borderBottom: '1px solid #f0f0f0',
                backgroundColor: '#fff',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#fafafa'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#fff'}
        >
            {/* Student Name */}
            <div style={{ flex: 1, fontSize: '16px', color: '#333' }}>
                {student.fullName}
            </div>
            
            {/* Note Toggle Button */}
            <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={() => {
                    toggleNoteArea(student.id);
                    // X seçilirse tik kalkar
                    if (!expandedNotes[student.id]) {
                        handleStatusChange(student.id, 'absent');
                    }
                }}
                style={{
                    color: student.status === 'absent' ? '#ff4d4f' : '#d9d9d9',
                    border: 'none',
                    padding: '4px 8px',
                    minWidth: 'auto',
                    marginRight: '8px'
                }}
            />
            
            {/* Status Checkmark */}
            <div
                onClick={() => {
                    handleStatusChange(student.id, student.status === 'present' ? 'absent' : 'present');
                    // Tik atınca not alanını kapat
                    if (expandedNotes[student.id]) {
                        setExpandedNotes(prev => ({
                            ...prev,
                            [student.id]: false
                        }));
                    }
                }}
                style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    border: '2px solid #52c41a',
                    backgroundColor: student.status === 'present' ? '#52c41a' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                }}
            >
                {student.status === 'present' && (
                    <CheckOutlined style={{ color: '#fff', fontSize: '14px' }} />
                )}
            </div>
        </div>
    );

    // Eğer seçili branch yoksa uyarı göster
    if (!selectedBranch) {
        return (
            <Flex vertical gap="middle" style={{ padding: '16px' }}>
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <h3>Lütfen önce bir branş seçiniz</h3>
                    <p>Yoklama almak için önce spor branşı sayfasından bir branş seçmelisiniz.</p>
                </div>
            </Flex>
        );
    }

    return (
        <div className="page-container">
            <Collapse 
                activeKey={filterExpanded ? ['1'] : []} 
                onChange={(keys) => setFilterExpanded(keys.includes('1'))}
                style={{ marginBottom: 16 }}
            >
                <Panel 
                    header={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <FilterOutlined />
                            <span>Filtre Seçenekleri</span>
                        </div>
                    } 
                    key="1"
                >
                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} sm={12} md={6}>
                            <Text strong>Tarih:</Text>
                            <DatePicker
                                value={selectedDate}
                                onChange={setSelectedDate}
                                format="DD/MM/YYYY"
                                style={{ width: '100%', marginTop: 8 }}
                                inputReadOnly={true}
                            />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Text strong>Filtre Tipi:</Text>
                            <Select
                                value={filterType}
                                onChange={handleFilterChange}
                                style={{ width: '100%', marginTop: 8 }}
                            >
                                <Option value="group">
                                    <TeamOutlined /> Grup
                                </Option>
                                <Option value="ageGroup">
                                    <UserOutlined /> Yaş Grubu
                                </Option>
                            </Select>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            {filterType === 'ageGroup' ? (
                                <>
                                    <Text strong>Yaş Grubu:</Text>
                                    <Select
                                        value={selectedAgeGroup}
                                        onChange={handleAgeGroupChange}
                                        placeholder="Yaş grubu seçin"
                                        style={{ width: '100%', marginTop: 8 }}
                                        allowClear
                                    >
                                        {ageGroups.map(ageGroup => (
                                            <Option key={ageGroup.id} value={ageGroup.id}>
                                                {ageGroup.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </>
                            ) : (
                                <>
                                    <Text strong>Grup:</Text>
                                    <Select
                                        value={selectedGroup}
                                        onChange={handleGroupChange}
                                        placeholder="Grup seçin"
                                        style={{ width: '100%', marginTop: 8 }}
                                        allowClear
                                    >
                                        {groups.map(group => (
                                            <Option key={group.id} value={group.id}>
                                                {group.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </>
                            )}
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Text strong>Durum:</Text>
                                <div>
                                    <Tag color="green" icon={<CheckOutlined />}>
                                        Var: {attendanceData.filter(m => m.status === 'present').length}
                                    </Tag>
                                    <Tag color="red" icon={<CloseOutlined />}>
                                        Yok: {attendanceData.filter(m => m.status === 'absent').length}
                                    </Tag>
                                </div>
                            </Space>
                        </Col>
                    </Row>
                </Panel>
            </Collapse>

            {/* Toplu Yoklama Butonları */}
            {attendanceData && attendanceData.length > 0 && (
                <Card style={{ marginBottom: 16 }}>
                    <Row gutter={[8, 8]} align="middle" justify="space-between">
                        <Col>
                            <Text strong>Toplu Yoklama:</Text>
                        </Col>
                        <Col>
                            <Space size="small">
                                <Button
                                    size="small"
                                    icon={<CheckOutlined />}
                                    onClick={() => handleBulkAttendance(true)}
                                    style={{ 
                                        borderColor: '#52c41a',
                                        color: '#52c41a'
                                    }}
                                >
                                    Hepsini Var
                                </Button>
                                <Button
                                    size="small"
                                    icon={<CloseOutlined />}
                                    onClick={() => handleBulkAttendance(false)}
                                    style={{ 
                                        borderColor: '#ff4d4f',
                                        color: '#ff4d4f'
                                    }}
                                >
                                    Hepsini Yok
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Card>
            )}

            {/* Custom Student List */}
            {attendanceData && attendanceData.length > 0 ? (
                <Card style={{ marginBottom: 16 }}>
                    <div style={{ 
                        maxHeight: '500px', 
                        overflowY: 'auto',
                        border: '1px solid #f0f0f0',
                        borderRadius: '8px'
                    }}>
                        {attendanceData.map((student, index) => (
                            <div key={student.id}>
                                <StudentListItem student={student} index={index} />
                                {/* Note Area */}
                                {expandedNotes[student.id] && (
                                    <div style={{
                                        padding: '12px 16px',
                                        backgroundColor: '#fafafa',
                                        borderBottom: '1px solid #f0f0f0'
                                    }}>
                                        <Input
                                            placeholder="Not ekle..."
                                            value={student.note}
                                            onChange={(e) => handleNoteChange(student.id, e.target.value)}
                                            size="small"
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </Card>
            ) : (
                <Card style={{ textAlign: 'center', padding: '40px' }}>
                    {loading ? <Spin size="large" /> : 'Seçilen kriterlere uygun öğrenci bulunmamaktadır'}
                </Card>
            )}

            {/* Yoklama Kaydet Butonu */}
            {attendanceData && attendanceData.length > 0 && (
                <Card style={{ marginTop: 16 }}>
                    <Row gutter={[16, 16]} justify="center" align="middle">
                        <Col xs={24} sm={12} md={3}>
                            <Text strong style={{ display: 'block', textAlign: 'center', marginBottom: 4, fontSize: 14 }}>
                                Eğitmen:
                            </Text>
                        </Col>
                        <Col xs={24} sm={12} md={5}>
                            <Select
                                value={selectedTrainer}
                                onChange={handleTrainerChange}
                                placeholder="Eğitmen seçin (opsiyonel)"
                                style={{ width: '100%', fontSize: 14, minWidth: 120, maxWidth: 180, height: 32 }}
                                allowClear
                                size="small"
                            >
                                {trainers.map(trainer => (
                                    <Option key={trainer.id} value={trainer.id}>
                                        {trainer.fullName}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} sm={24} md={6}>
                            <Checkbox checked={sendAbsenceMsg} onChange={e => setSendAbsenceMsg(e.target.checked)} style={{ fontSize: 16 }}>
                                Yoklara mesaj gönder
                            </Checkbox>
                        </Col>
                        <Col xs={24} sm={24} md={6}>
                            {sendAbsenceMsg && (
                                <Input.TextArea
                                    value={absenceMessage}
                                    onChange={(e) => setAbsenceMessage(e.target.value)}
                                    placeholder="Yok olan öğrencilere gönderilecek mesajı yazın..."
                                    rows={2}
                                    style={{ fontSize: 14 }}
                                    size="small"
                                />
                            )}
                        </Col>
                        <Col xs={24} sm={24} md={6}>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={handleSaveAttendance}
                                loading={saving}
                                size="large"
                                style={{ 
                                    width: '100%',
                                    backgroundColor: '#f65700',
                                    borderColor: '#f65700'
                                }}
                            >
                                {saving ? 'Kaydediliyor...' : 'Yoklamayı Kaydet'}
                            </Button>
                        </Col>
                    </Row>
                </Card>
            )}
        </div>
    );
};

export default Attendance; 